import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { cn } from '../../../shared/lib/cn';
import { FactList, FactRow } from '../../../shared/ui/fact-list';
import { Note } from '../../../shared/ui/note';
import { SectionPanel } from '../../../shared/ui/section-panel';
import { ErrorState, LoadingState } from '../../../shared/ui/states';
import { ShortId } from '../../../shared/ui/table';
import {
  listPresetVersions,
  presetUsage,
  type TAuthPresetVersion,
} from '../../../entities/auth-preset';
import { listSources } from '../../../entities/source';
import { MoveSources } from './move-sources';

export const VersionsPanel = ({
  presetId,
  builtin,
  onOpenVersion,
}: {
  presetId: string;
  builtin: boolean;
  onOpenVersion?: (version: TAuthPresetVersion) => void;
}) => {
  const versions = useQuery({
    queryKey: ['auth-preset-versions-all', presetId],
    queryFn: () => listPresetVersions(presetId),
  });
  const usage = useQuery({
    queryKey: ['auth-preset-usage', presetId],
    queryFn: () => presetUsage(presetId),
  });
  const sources = useQuery({ queryKey: ['sources'], queryFn: listSources });

  const rows = [...(versions.data ?? [])].sort((a, b) => b.version - a.version);
  const [selected, setSelected] = useState<number | null>(null);
  const current = rows.find((row) => row.version === selected) ?? rows[0];
  const sitting = (usage.data ?? []).filter((row) => row.version === current?.version);

  const nameOf = (sourceId: string) =>
    sources.data?.data.find((source) => source.id === sourceId)?.name ?? null;

  return (
    <SectionPanel title="Versions" note={rows.length > 0 ? `${rows.length} saved` : undefined}>
      {versions.isPending && <LoadingState rows={3} className="border-0" />}
      {versions.isError && <ErrorState error={versions.error} />}

      {rows.map((row) => (
        <button
          key={row.id}
          type="button"
          onClick={() => {
            setSelected(row.version);
            onOpenVersion?.(row);
          }}
          className={cn(
            'flex w-full items-center gap-inset border-b border-border-row px-3 py-2 text-left',
            row.version === current?.version ? 'bg-hover' : 'hover:bg-hover',
          )}
        >
          <span className="w-col-try shrink-0 font-mono text-data text-primary">
            v{row.version}
          </span>
          <span className="min-w-0 grow truncate text-meta text-muted">
            {row.comment ?? 'no note left'}
          </span>
          <span className="shrink-0 font-mono text-micro text-faint">
            {row.usedByCount === 0
              ? '—'
              : `${row.usedByCount} source${row.usedByCount === 1 ? '' : 's'}`}
          </span>
        </button>
      ))}

      <div className="p-input">
        <p className="mb-2 font-mono text-micro text-faint">
          Sources checking with v{current?.version ?? '—'}
        </p>
        {usage.isPending ? (
          <LoadingState rows={1} className="border-0" />
        ) : sitting.length === 0 ? (
          <p className="text-meta text-muted">
            Nobody is pinned to this version, so changing it affects no live traffic.
          </p>
        ) : (
          <FactList>
            {sitting.map((row) => (
              <FactRow
                key={row.sourceAuthMethodId}
                label={nameOf(row.sourceId) ?? <ShortId id={row.sourceId} />}
              >
                v{row.version}
              </FactRow>
            ))}
          </FactList>
        )}

        {!builtin && current && (
          <div className="mt-4">
            <MoveSources
              presetId={presetId}
              version={current.version}
              spec={current.spec}
              usage={usage.data ?? []}
            />
          </div>
        )}
      </div>

      <Note className="mx-input mb-input">
        Moving is an edit on those sources, not on this scheme. It changes how real incoming
        requests are checked, so you pick which sources go.
      </Note>
    </SectionPanel>
  );
};
